/**
 * FW-Kantine Auth Service
 * =======================
 * Handles JWT token storage and API authentication
 */

const TOKEN_KEY = 'fw_kantine_token';
const USER_KEY = 'fw_kantine_user';
const OPERATOR_ACTIVE_KEY = 'fw_kantine_operator_active';
const APP_SESSION_KEYS = [
  'fw_kantine_last_department',
  'fw_kantine_is_department_admin',
  'fw_kantine_is_master_admin',
];
export const SESSION_EXPIRED_EVENT = 'fw-session-expired';
export const PAYMENT_ACCESS_BLOCKED_EVENT = 'fw-payment-access-blocked';
const SESSION_EXPIRED_MESSAGE = 'Ihre Sitzung ist abgelaufen. Bitte erneut einloggen.';

/**
 * Store authentication data
 */
export const setAuth = (token, userData) => {
  if (typeof window !== 'undefined') {
    window.__fwSessionExpiredNotified = false;
  }
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
  if (userData) {
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  }
};

/**
 * Get stored token
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored user data
 */
export const getUser = () => {
  const userData = localStorage.getItem(USER_KEY);
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Clear authentication data (logout)
 */
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  APP_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem(OPERATOR_ACTIVE_KEY);
};

export const setOperatorSessionActive = (active) => {
  if (active) {
    sessionStorage.setItem(OPERATOR_ACTIVE_KEY, 'true');
  } else {
    sessionStorage.removeItem(OPERATOR_ACTIVE_KEY);
  }
};

/** End an invalid session once and tell the application to show the login. */
export const expireSession = () => {
  clearAuth();
  if (typeof window === 'undefined' || window.__fwSessionExpiredNotified) return;

  window.__fwSessionExpiredNotified = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  window.alert(SESSION_EXPIRED_MESSAGE);
};

/** Publish an authoritative Payment block and optionally end local auth. */
export const notifyPaymentAccessBlocked = (
  detail,
  { clearSession = false } = {}
) => {
  if (clearSession) clearAuth();
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PAYMENT_ACCESS_BLOCKED_EVENT, { detail }));
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Get authorization header for API calls
 */
export const getAuthHeader = () => {
  const token = getToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

/**
 * Check if token is expired (client-side check)
 * JWT payload contains exp as Unix timestamp
 */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;
  
  try {
    // Decode JWT payload (middle part)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    return Date.now() > expiry;
  } catch {
    return true;
  }
};

/**
 * Get token expiry time
 */
export const getTokenExpiry = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
};

/**
 * Create axios interceptor for automatic token handling
 * Usage: setupAxiosInterceptor(axios);
 */
export const setupAxiosInterceptor = (axiosInstance) => {
  // Request interceptor - add token to all requests
  axiosInstance.interceptors.request.use(
    (config) => {
      const isLoginEndpoint = config.url?.includes('/login/');
      const token = getToken();
      if (token && !isLoginEndpoint && isTokenExpired()) {
        expireSession();
        const error = new Error(SESSION_EXPIRED_MESSAGE);
        error.code = 'FW_SESSION_EXPIRED';
        return Promise.reject(error);
      }
      if (token && !isLoginEndpoint) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - any rejected authenticated request ends the session.
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const isLoginEndpoint = error.config?.url?.includes('/login/');
      const paymentDetail = error.response?.data?.detail;
      if (
        error.response?.status === 403 &&
        !isLoginEndpoint &&
        paymentDetail?.code === 'PAYMENT_ACCESS_BLOCKED'
      ) {
        notifyPaymentAccessBlocked(paymentDetail, { clearSession: true });
      }
      if (error.response?.status === 401) {
        const hasOperatorSession = sessionStorage.getItem(OPERATOR_ACTIVE_KEY) === 'true';
        if (!isLoginEndpoint && (getToken() || hasOperatorSession)) {
          console.warn('Authentication rejected - returning to login');
          expireSession();
        }
      }
      return Promise.reject(error);
    }
  );
};

export default {
  setAuth,
  setOperatorSessionActive,
  getToken,
  getUser,
  clearAuth,
  notifyPaymentAccessBlocked,
  expireSession,
  isAuthenticated,
  getAuthHeader,
  isTokenExpired,
  getTokenExpiry,
  SESSION_EXPIRED_EVENT,
  PAYMENT_ACCESS_BLOCKED_EVENT,
  setupAxiosInterceptor
};
